import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Clock, Globe, BarChart3, ArrowRight, Award, LayoutDashboard, Sparkles, Clock3, ShieldCheck, Route } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { FLAGSHIP_COURSES, INSTRUCTORS, LANGUAGES, LEARNING_PATHS } from "../data/flagshipCourses";
import { EXPANSION_COURSES } from "../data/expansionCourses";
import { useContent } from "../hooks/useContent";
import LearningCoach from "../components/LearningCoach";

const ALL_RICH = [...FLAGSHIP_COURSES, ...EXPANSION_COURSES];

const LC_DEFAULT = {
  eyebrow: "TAVEN Learning Center", title: "The TAVEN Academy",
  subtitle: "A real school inside the ecosystem — learn e-commerce, marketplace selling, affiliate marketing, and leadership with guided instructors, quizzes, and official certificates. Available in English, Français, Kreyòl & Español.",
  paths_title: "Learning Paths",
  paths_subtitle: "Guided journeys that take you step by step from beginner to confident.",
  featured_title: "Featured Flagship Courses",
  featured_subtitle: "Fully furnished, ready-to-teach programs with guided instructors, voice-over lessons, quizzes and certificates.",
  coming_title: "Coming Soon Programs",
};

export const EducationHub = () => {
  const [published, setPublished] = useState([]);
  const [coming, setComing] = useState([]);
  const [cat, setCat] = useState("All");
  const lc = useContent("learning_center", LC_DEFAULT);

  useEffect(() => {
    api.get("/courses")
      .then(({ data }) => { setPublished(data.published || []); setComing(data.coming_soon || []); })
      .catch(() => { setPublished(FLAGSHIP_COURSES.map((c) => ({ slug: c.slug }))); });
  }, []);

  // Merge published registry slugs with rich frontend content (flagship + expansion).
  const courses = useMemo(() => {
    const pubSlugs = new Set(published.map((p) => p.slug));
    return ALL_RICH.filter((c) => pubSlugs.has(c.slug) || published.length === 0);
  }, [published]);

  const cats = useMemo(() => ["All", ...Array.from(new Set(courses.map((c) => c.category)))], [courses]);
  const filtered = cat === "All" ? courses : courses.filter((c) => c.category === cat);

  return (
    <div data-testid="education-hub-page">
      <PageHero eyebrow={lc.eyebrow} title={lc.title} subtitle={lc.subtitle}>
        <div className="flex flex-wrap gap-2 items-center">
          {LANGUAGES.map((l) => <span key={l} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/20 px-3 py-1.5"><Globe className="w-3.5 h-3.5 text-taven-gold" /> {l}</span>)}
          <Link to="/account" data-testid="student-dashboard-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-taven-gold text-black px-3 py-1.5 hover:bg-white transition-colors"><LayoutDashboard className="w-3.5 h-3.5" /> Student Dashboard</Link>
          <Link to="/verify" data-testid="verify-cert-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-white/25 text-white px-3 py-1.5 hover:border-taven-gold transition-colors"><ShieldCheck className="w-3.5 h-3.5 text-taven-gold" /> Verify a Certificate</Link>
        </div>
      </PageHero>

      <section className="py-10 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
        {/* Learning Paths */}
        <div className="flex items-center gap-2 mb-1"><Route className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">{lc.paths_title}</h2></div>
        <p className="text-sm text-muted-foreground mb-6">{lc.paths_subtitle}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="learning-paths">
          {LEARNING_PATHS.map((p) => {
            const steps = p.steps.map((s) => FLAGSHIP_COURSES.find((c) => c.slug === s)).filter(Boolean);
            const first = steps[0];
            return (
              <Link key={p.key} to={first ? `/education/${first.slug}` : "/education"} data-testid={`path-${p.key}`} className="border border-black/10 bg-white p-5 hover:border-taven-gold hover-lift group">
                <div className="w-10 h-10 bg-taven-black text-taven-gold flex items-center justify-center"><Route className="w-5 h-5" /></div>
                <p className="font-black font-display mt-3 group-hover:text-taven-gold">{p.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.blurb}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-taven-gold mt-3">{steps.length} course{steps.length !== 1 ? "s" : ""} · Beginner → Advanced</p>
              </Link>
            );
          })}
        </div>

        {/* Featured flagship courses */}
        <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">{lc.featured_title}</h2></div>
        <p className="text-sm text-muted-foreground mb-6">{lc.featured_subtitle}</p>

        <div className="flex flex-wrap gap-2 mb-8">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`edu-cat-${c.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${cat === c ? "bg-taven-black text-white border-taven-black" : "bg-white border-black/15 hover:border-taven-gold"}`}>{c}</button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5">
          {filtered.map((c) => {
            const ins = INSTRUCTORS[c.instructor];
            return (
              <Link key={c.slug} to={`/education/${c.slug}`} onClick={() => track("course_enroll", { item_id: c.slug, item_name: c.title })} data-testid={`course-card-${c.slug}`} className="bg-white border border-black/5 hover:border-taven-gold hover-lift flex flex-col group">
                <div className="p-6 flex-grow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-taven-gold/15 text-taven-gold px-2 py-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Flagship</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 px-2 py-1">Free</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-taven-gray px-2 py-1">{c.level}</span>
                  </div>
                  <h3 className="text-lg font-black font-display mt-4 group-hover:text-taven-gold">{c.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.summary}</p>
                  {ins && (
                    <div className="flex items-center gap-2.5 mt-4">
                      <img src={ins.avatar} alt={ins.name} className="w-9 h-9 rounded-full object-cover border border-taven-gold" />
                      <div><p className="text-xs font-bold leading-none">{ins.name}</p><p className="text-[11px] text-muted-foreground mt-0.5">{ins.title}</p></div>
                    </div>
                  )}
                </div>
                <div className="px-6 py-4 border-t border-black/5 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.duration}</span>
                  <span className="flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> {c.modules.length} modules</span>
                  <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Certificate</span>
                  <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> {c.languages.length}</span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Coming soon roadmap */}
        {coming.length > 0 && (
          <div className="mt-16" data-testid="coming-soon">
            <div className="flex items-center gap-2 mb-1"><Clock3 className="w-5 h-5 text-taven-gold" /><h2 className="text-lg font-black font-display uppercase tracking-wide">{lc.coming_title}</h2></div>
            <p className="text-sm text-muted-foreground mb-6">We're building a full school of {coming.length + filtered.length}+ courses. New programs are published only once they meet TAVEN's quality standard — furnished with full modules, lessons, quizzes, resources and certificates.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {coming.map((c) => (
                <div key={c.slug} className="border border-dashed border-black/15 bg-white/60 p-4" data-testid={`coming-${c.slug}`}>
                  <GraduationCap className="w-6 h-6 text-black/30" />
                  <p className="text-sm font-bold mt-2">{c.title}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{c.category}</p>
                  <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider text-taven-gold">In development</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      <LearningCoach />
    </div>
  );
};

export default EducationHub;
